/**
 * Security Score Calculator
 * Computes a 0-100 security health score per server based on available metrics.
 * Scores are weighted across categories: protection, access, updates, and encryption.
 */

export interface SecurityCheckResult {
  check: string;
  category: "protection" | "access" | "updates" | "encryption" | "network";
  status: "pass" | "warning" | "fail" | "unknown";
  points: number;
  maxPoints: number;
  detail: string;
}

export interface SecurityScoreResult {
  score: number;
  grade: "A" | "B" | "C" | "D" | "F";
  checks: SecurityCheckResult[];
  breakdown: { category: string; earned: number; max: number }[];
}

function getGrade(score: number): "A" | "B" | "C" | "D" | "F" {
  if (score >= 90) return "A";
  if (score >= 75) return "B";
  if (score >= 60) return "C";
  if (score >= 40) return "D";
  return "F";
}

export function calculateSecurityScore(server: any): SecurityScoreResult {
  const isLinux = server.os === "linux";
  const checks: SecurityCheckResult[] = [];

  // ── Protection ──────────────────────────────────

  // Firewall
  if (server.firewall_enabled === true) {
    checks.push({ check: "Firewall", category: "protection", status: "pass", points: 15, maxPoints: 15, detail: "Firewall is active" });
  } else if (server.firewall_enabled === false) {
    checks.push({ check: "Firewall", category: "protection", status: "fail", points: 0, maxPoints: 15, detail: "Firewall is disabled" });
  } else {
    checks.push({ check: "Firewall", category: "protection", status: "unknown", points: 5, maxPoints: 15, detail: "Firewall status unknown" });
  }

  if (!isLinux) {
    // Windows Defender
    if (server.windows_defender_enabled === true) {
      if (server.windows_defender_updated === true) {
        checks.push({ check: "Antivirus", category: "protection", status: "pass", points: 15, maxPoints: 15, detail: "Defender active & updated" });
      } else {
        checks.push({ check: "Antivirus", category: "protection", status: "warning", points: 8, maxPoints: 15, detail: "Defender active but signatures outdated" });
      }
    } else if (server.windows_defender_enabled === false) {
      checks.push({ check: "Antivirus", category: "protection", status: "fail", points: 0, maxPoints: 15, detail: "Defender is disabled" });
    } else {
      checks.push({ check: "Antivirus", category: "protection", status: "unknown", points: 5, maxPoints: 15, detail: "Defender status unknown" });
    }
  } else {
    // Linux antivirus (ClamAV etc.)
    if (server.antivirus_product) {
      checks.push({ check: "Antivirus", category: "protection", status: "pass", points: 10, maxPoints: 10, detail: `${server.antivirus_product} active` });
    } else {
      checks.push({ check: "Antivirus", category: "protection", status: "warning", points: 5, maxPoints: 10, detail: "No antivirus detected (optional for Linux)" });
    }
  }

  // ── Encryption ──────────────────────────────────

  if (!isLinux) {
    // Secure Boot
    if (server.secure_boot_enabled === true) {
      checks.push({ check: "Secure Boot", category: "encryption", status: "pass", points: 10, maxPoints: 10, detail: "Secure Boot enabled" });
    } else if (server.secure_boot_enabled === false) {
      checks.push({ check: "Secure Boot", category: "encryption", status: "fail", points: 0, maxPoints: 10, detail: "Secure Boot disabled" });
    } else {
      checks.push({ check: "Secure Boot", category: "encryption", status: "unknown", points: 3, maxPoints: 10, detail: "Secure Boot status unknown" });
    }

    // TPM
    if (server.tpm_enabled === true) {
      checks.push({ check: "TPM", category: "encryption", status: "pass", points: 10, maxPoints: 10, detail: "TPM is enabled" });
    } else if (server.tpm_enabled === false) {
      checks.push({ check: "TPM", category: "encryption", status: "fail", points: 0, maxPoints: 10, detail: "TPM is disabled" });
    } else {
      checks.push({ check: "TPM", category: "encryption", status: "unknown", points: 3, maxPoints: 10, detail: "TPM status unknown" });
    }

    // BitLocker
    const bitlocker = server.bitlocker_status;
    if (bitlocker && Array.isArray(bitlocker)) {
      const osVol = bitlocker.find((v: any) => v.mount_point === "C:");
      if (osVol?.protection_status === "On") {
        checks.push({ check: "Disk Encryption", category: "encryption", status: "pass", points: 10, maxPoints: 10, detail: "BitLocker enabled on OS drive" });
      } else {
        checks.push({ check: "Disk Encryption", category: "encryption", status: "fail", points: 0, maxPoints: 10, detail: "OS drive not encrypted" });
      }
    } else {
      checks.push({ check: "Disk Encryption", category: "encryption", status: "unknown", points: 3, maxPoints: 10, detail: "BitLocker status unknown" });
    }
  }

  // ── Access Control ──────────────────────────────

  // Failed logins
  const failedLogins = server.failed_login_last_24h;
  if (failedLogins !== null && failedLogins !== undefined) {
    if (failedLogins <= 5) {
      checks.push({ check: "Failed Logins", category: "access", status: "pass", points: 15, maxPoints: 15, detail: `${failedLogins} failed logins in 24h` });
    } else if (failedLogins <= 20) {
      checks.push({ check: "Failed Logins", category: "access", status: "warning", points: 8, maxPoints: 15, detail: `${failedLogins} failed logins in 24h` });
    } else {
      checks.push({ check: "Failed Logins", category: "access", status: "fail", points: 0, maxPoints: 15, detail: `${failedLogins} failed logins in 24h — possible brute force` });
    }
  } else {
    checks.push({ check: "Failed Logins", category: "access", status: "unknown", points: 7, maxPoints: 15, detail: "Login data unavailable" });
  }

  // Admin accounts
  const adminCount = server.admin_accounts_count;
  if (adminCount !== null && adminCount !== undefined) {
    if (adminCount <= 3) {
      checks.push({ check: isLinux ? "Root/Sudo Users" : "Admin Accounts", category: "access", status: "pass", points: 10, maxPoints: 10, detail: `${adminCount} privileged accounts` });
    } else if (adminCount <= 7) {
      checks.push({ check: isLinux ? "Root/Sudo Users" : "Admin Accounts", category: "access", status: "warning", points: 5, maxPoints: 10, detail: `${adminCount} privileged accounts — consider reducing` });
    } else {
      checks.push({ check: isLinux ? "Root/Sudo Users" : "Admin Accounts", category: "access", status: "fail", points: 0, maxPoints: 10, detail: `${adminCount} privileged accounts — too many` });
    }
  } else {
    checks.push({ check: isLinux ? "Root/Sudo Users" : "Admin Accounts", category: "access", status: "unknown", points: 3, maxPoints: 10, detail: "Account data unavailable" });
  }

  // SMBv1 (Windows only)
  if (!isLinux && server.smbv1_enabled !== undefined) {
    if (server.smbv1_enabled === false) {
      checks.push({ check: "SMBv1 Disabled", category: "network", status: "pass", points: 5, maxPoints: 5, detail: "Legacy SMBv1 is disabled" });
    } else {
      checks.push({ check: "SMBv1 Disabled", category: "network", status: "fail", points: 0, maxPoints: 5, detail: "SMBv1 is still enabled — security risk" });
    }
  }

  // ── Updates ──────────────────────────────────

  const pendingUpdates = server.pending_updates_count ?? server.pending_security_updates;
  if (pendingUpdates !== null && pendingUpdates !== undefined) {
    if (pendingUpdates === 0) {
      checks.push({ check: "System Updates", category: "updates", status: "pass", points: 15, maxPoints: 15, detail: "All updates installed" });
    } else if (pendingUpdates <= 5) {
      checks.push({ check: "System Updates", category: "updates", status: "warning", points: 8, maxPoints: 15, detail: `${pendingUpdates} pending updates` });
    } else {
      checks.push({ check: "System Updates", category: "updates", status: "fail", points: 0, maxPoints: 15, detail: `${pendingUpdates} pending updates — patch now` });
    }
  } else {
    checks.push({ check: "System Updates", category: "updates", status: "unknown", points: 7, maxPoints: 15, detail: "Update data unavailable" });
  }

  // ── Compute totals ──────────────────────────────

  const totalEarned = checks.reduce((s, c) => s + c.points, 0);
  const totalMax = checks.reduce((s, c) => s + c.maxPoints, 0);
  const score = totalMax > 0 ? Math.round((totalEarned / totalMax) * 100) : 0;

  // Category breakdown
  const categories = [...new Set(checks.map((c) => c.category))];
  const breakdown = categories.map((cat) => {
    const catChecks = checks.filter((c) => c.category === cat);
    return {
      category: cat,
      earned: catChecks.reduce((s, c) => s + c.points, 0),
      max: catChecks.reduce((s, c) => s + c.maxPoints, 0),
    };
  });

  return { score, grade: getGrade(score), checks, breakdown };
}
